import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getSeeCoursesByType } from "../../../services/coursesApi";
import CourseCard from "../cards/CourseCard";
import InfiniteScroll from "react-infinite-scroller";
import { useInfiniteQuery } from "react-query";
import NewCourseCardShimmer from "../shimmers/NewCourseCardShimmer";
import MixpanelManager from "../../../constants/MixpanelManager";
import { MixpanelEvents } from "../../../constants/MixpanelEvents";

function getGridColumnCount() {
  if (typeof window === "undefined") return 2;
  const width = window.innerWidth;
  if (width >= 2200) return 3;
  if (width > 991) return 2;
  return 1;
}

function useGridColumns() {
  const [columns, setColumns] = useState(getGridColumnCount);

  useEffect(() => {
    const handleResize = () => {
      setColumns(getGridColumnCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
}

const MyCourses = ({ count, type }) => {
  const user = useMemo(() => JSON.parse(localStorage.getItem("SplashData")), []);
  const user_id = user?.user?.id;
  const columns = useGridColumns();
  const initialShimmerCount = count || (columns === 1 ? 4 : columns * 2);
  const loaderShimmerCount = columns === 1 ? 2 : columns * 2;

  // Identify user if not already identified
  useEffect(() => {
    if (user?.user) {
      console.log('🔍 Identifying User in My Courses');
      MixpanelManager.identify(
        user.user.id,
        user.user.email,
        user.user.name,
        user.user.profile_image
      );
      console.log('✅ User Identified in My Courses');
    }
  }, [user?.user]);

  // Track myCourses event on component mount
  useEffect(() => {
    console.log('📚 My Courses Event - Tracking');
    MixpanelManager.track(MixpanelEvents.myCourses, {
      user_id: user_id,
      type: type,
    });
    MixpanelManager.track(MixpanelEvents.viewCourseContent, { content_type: 'my_courses' });
    console.log('✅ My Courses Event Tracked');
  }, [type, user_id]);
  const queryFn = useCallback(
    ({ pageParam = 1 }) => getSeeCoursesByType({ page: pageParam, type }),
    [type]
  );
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["myCourses", type],
      queryFn: queryFn,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      enabled: !!type,      
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,      
      cacheTime: 0, 
      keepPreviousData: false, 
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const courses = data?.pages?.flatMap((page) => page.data.data);

  return (
    <>
      {isLoading ? (
        <div className="cr-courses-grid">
          {Array.from({ length: initialShimmerCount }).map((_, index) => (
            <NewCourseCardShimmer width="100%" key={`my-init-${index}`} />
          ))}
        </div>
      ) : courses?.length > 0 ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={() => {
            if (!isFetchingNextPage && hasNextPage) {
              MixpanelManager.track(MixpanelEvents.viewCourseContent, { action: 'load_more', type: 'my_courses' });
              fetchNextPage();
            }
          }}
          hasMore={Boolean(hasNextPage)}
          useWindow={true}
          threshold={350}
          loader={
            <div className="cr-courses-grid mt-3" key="my-infinite-loader">
              {Array.from({ length: loaderShimmerCount }).map((_, index) => (
                <NewCourseCardShimmer width="100%" key={`my-load-${index}`} />
              ))}
            </div>
          }
        >
          <div className="cr-courses-grid">
            {courses?.map((course, index) => (
              <CourseCard key={course.id || index} course={course} width="100%" />
            ))}
          </div>
        </InfiniteScroll>
      ) : (
        <div className="centered-message-container">
          <div className="centered-message-text">
            <p className="fs-16 roboto-medium mb-1 text-muted">
              No Available Courses!
            </p>
            <p className="roboto-medium mb-1 text-muted">
              We're sorry, but there are currently no My Course available <br />{" "}
              at the moment. Please check back later or explore other services.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
export default React.memo(MyCourses);
